import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import axios from "axios";
import { Eye, EyeOff, ArrowLeft, Shield, Users, Mail, Store } from "lucide-react";

const LOGO_URL = "/achraj_logo.png";
const HERO_IMAGE = "https://static.prod-images.emergentagent.com/jobs/a5b8e8e1-bbb3-482b-9315-930386677976/images/be592794ef7107c3305196753828a8c62cdd8754a907c532e1eda6bfd22863a0.png";

export default function LoginPage() {
  const [memberForm, setMemberForm] = useState({ member_id: "", password: "" });
  const [adminForm, setAdminForm] = useState({ email: "", password: "" });
  const [franchiseForm, setFranchiseForm] = useState({ franchise_code: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirect");

  const handleMemberLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await axios.post(`${API}/auth/member/login`, memberForm, {
        timeout: 30000
      });
      login(res.data.token, res.data.user);
      toast.success("Login successful!");
      navigate(redirectTo || "/member");
    } catch (err) {
      console.error("Member login error:", err);
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        toast.error("Connection timeout. Please try again.");
      } else if (err.code === 'ERR_NETWORK') {
        toast.error("Network error. Please check your connection.");
      } else {
        toast.error(err.response?.data?.detail || "Login failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await axios.post(`${API}/auth/admin/login`, adminForm, {
        timeout: 30000
      });
      login(res.data.token, res.data.user);
      toast.success("Login successful!");
      navigate(redirectTo || "/admin");
    } catch (err) {
      console.error("Admin login error:", err);
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        toast.error("Connection timeout. Please try again.");
      } else if (err.code === 'ERR_NETWORK') {
        toast.error("Network error. Please check your connection.");
      } else {
        toast.error(err.response?.data?.detail || "Login failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFranchiseLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await axios.post(`${API}/auth/franchise/login`, franchiseForm, {
        timeout: 30000
      });
      login(res.data.token, res.data.user);
      toast.success("Login successful! / लॉगिन सफल!");
      navigate(redirectTo || "/franchise");
    } catch (err) {
      console.error("Franchise login error:", err);
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        toast.error("Connection timeout. Please try again.");
      } else if (err.code === 'ERR_NETWORK') {
        toast.error("Network error. Please check your connection.");
      } else {
        toast.error(err.response?.data?.detail || "Login failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* Left Side - Indian Business Professionals */}
      <div className="hidden md:block relative">
        <img 
          src={HERO_IMAGE}
          alt="Indian Business Professionals"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/40 to-[#E65100]/50"></div>
        <div className="absolute inset-0 flex flex-col justify-center p-12">
          <Link to="/" className="flex items-center gap-3 mb-8">
            <img src={LOGO_URL} alt="Achraj Logo" className="h-16 w-16 rounded-full object-cover border-2 border-white/50 shadow-lg" />
          </Link>
          <h1 className="text-4xl font-bold text-white mb-2">Wapas Swagat Hai!</h1>
          <p className="text-[#D4AF37] text-xl font-semibold mb-4">Ghar baithe kamaai, bas ek ID se shuru karo</p>
          <p className="text-white/90 text-lg">
            Apna Member ID dalkar apna dashboard access karo aur network income track karo.
          </p>
          <div className="mt-8 p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
            <p className="text-white/80 text-sm">
              <strong>Member Login:</strong> Use your Member ID (e.g., ACJ00521) and last 4 digits of your mobile as password.
            </p>
          </div>
          <div className="mt-6 flex items-center gap-2 text-white">
            <Mail className="w-5 h-5 text-[#D4AF37]" />
            <a href="mailto:achrajaigroup@gmail.com" className="font-semibold hover:underline">achrajaigroup@gmail.com</a>
            <span className="text-white/60">| 24/7 Support</span>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex flex-col justify-center p-8 md:p-12 bg-slate-50">
        <div className="md:hidden mb-8">
          <Link to="/" className="inline-flex items-center text-slate-600 hover:text-slate-900">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
          </Link>
        </div>

        <div className="max-w-md mx-auto w-full">
          <Card className="border-0 shadow-xl">
            <CardHeader className="text-center">
              <div className="md:hidden flex justify-center mb-4">
                <img src={LOGO_URL} alt="Achraj Logo" className="h-14 w-14 rounded-full object-cover border-2 border-[#E65100] shadow-md" />
              </div>
              <CardTitle className="text-2xl font-bold">Sign In</CardTitle>
              <CardDescription>Access your Achraj account</CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="member" className="w-full">
                <TabsList className="grid w-full grid-cols-3 mb-6">
                  <TabsTrigger value="member" className="flex items-center gap-1 text-xs sm:text-sm">
                    <Users className="w-4 h-4" /> <span className="hidden sm:inline">Member</span>
                  </TabsTrigger>
                  <TabsTrigger value="franchise" className="flex items-center gap-1 text-xs sm:text-sm">
                    <Store className="w-4 h-4" /> <span className="hidden sm:inline">Franchise</span>
                  </TabsTrigger>
                  <TabsTrigger value="admin" className="flex items-center gap-1 text-xs sm:text-sm">
                    <Shield className="w-4 h-4" /> <span className="hidden sm:inline">Admin</span>
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="member">
                  <form onSubmit={handleMemberLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="member_id">Member ID / सदस्य आईडी</Label>
                      <Input
                        id="member_id"
                        type="text"
                        placeholder="ACJ00521"
                        data-testid="member-id-input"
                        value={memberForm.member_id}
                        onChange={(e) => setMemberForm({ ...memberForm, member_id: e.target.value.toUpperCase() })}
                        required
                        className="h-12"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="member_password">Password (Last 4 digits of mobile)</Label>
                      <div className="relative">
                        <Input
                          id="member_password"
                          type={showPassword ? "text" : "password"}
                          placeholder="••••"
                          maxLength={4}
                          data-testid="member-password-input"
                          value={memberForm.password}
                          onChange={(e) => setMemberForm({ ...memberForm, password: e.target.value })}
                          required
                          className="h-12 pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>

                    <Button
                      type="submit"
                      data-testid="member-login-btn"
                      disabled={loading}
                      className="w-full h-12 bg-gradient-to-r from-[#E65100] to-[#FF8F00] hover:from-[#D84315] hover:to-[#E65100] text-white font-semibold rounded-full"
                    >
                      {loading ? "Signing in..." : "Sign In as Member"}
                    </Button>
                  </form>
                </TabsContent>

                <TabsContent value="franchise">
                  <form onSubmit={handleFranchiseLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="franchise_code">Franchise Code / फ्रैंचाइज़ कोड</Label>
                      <Input
                        id="franchise_code"
                        type="text"
                        placeholder="FRN-JAU-001"
                        data-testid="franchise-code-input"
                        value={franchiseForm.franchise_code}
                        onChange={(e) => setFranchiseForm({ ...franchiseForm, franchise_code: e.target.value.toUpperCase() })}
                        required
                        className="h-12"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="franchise_password">Password / पासवर्ड</Label>
                      <div className="relative">
                        <Input
                          id="franchise_password"
                          type={showPassword ? "text" : "password"}
                          placeholder="••••••••"
                          data-testid="franchise-password-input"
                          value={franchiseForm.password}
                          onChange={(e) => setFranchiseForm({ ...franchiseForm, password: e.target.value })}
                          required
                          className="h-12 pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>

                    <Button
                      type="submit"
                      data-testid="franchise-login-btn"
                      disabled={loading}
                      className="w-full h-12 bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 text-white font-semibold rounded-full"
                    >
                      {loading ? "Signing in... / प्रवेश हो रहा है..." : "Sign In as Franchise / फ्रैंचाइज़ लॉगिन"}
                    </Button>
                  </form>
                </TabsContent>

                <TabsContent value="admin">
                  <form onSubmit={handleAdminLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="admin_email">Email</Label>
                      <Input
                        id="admin_email"
                        type="email"
                        placeholder="admin@achraj.com"
                        data-testid="admin-email-input"
                        value={adminForm.email}
                        onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
                        required
                        className="h-12"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="admin_password">Password</Label>
                      <div className="relative">
                        <Input
                          id="admin_password"
                          type={showPassword ? "text" : "password"}
                          placeholder="••••••••"
                          data-testid="admin-password-input"
                          value={adminForm.password}
                          onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })}
                          required
                          className="h-12 pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>

                    <Button
                      type="submit"
                      data-testid="admin-login-btn"
                      disabled={loading}
                      className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-full"
                    >
                      {loading ? "Signing in..." : "Sign In as Admin"}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>

              <div className="mt-6 text-center text-sm text-slate-600">
                Want to join?{" "}
                <Link to="/register" className="text-[#E65100] font-semibold hover:underline">
                  Register Now
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
